import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, connect } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import {
  DEFAULT_ORDER_SERVICE_TYPE,
  normalizeOrderServiceType,
  type OrderServiceType,
} from '@constants/orderServiceType';
import { hasPermission } from '@src/utilities/common';
import { submitOrder } from '@src/utilities/order';
import { useOnlineStatus } from '@src/app/hooks';
import { handleSearchFoodItems } from '@flows/POSPanel/SearchUtils';

import { TableTopBar, SearchBar, CategoryChips, MenuGrid, SearchResultsGrid, CartPanel, CartFab } from './components';
import { computeBillingSummary, buildOrderDetails } from './services/billingService';
import { addToCartData } from '@actions/userActions';
import { addItemToCart, updateItemQty, removeItem, executeMergeTable } from './services/cartService';
import { computeBadgeCounts, getMenuCategories, getMenuItems, getActiveCartItems } from './services/tableService';

import type {
    POSV2Props,
    CustomerDetails,
    FoodItem,
    Variant,
    VariantMap,
    CartItem,
    SearchResult,
    TableCart,
} from './types';

import './POSV2.scss';

const CART_INPUTS_STORAGE_KEY = 'cartInputsByTable';

const POSV2: React.FC<POSV2Props> = ({
    cart,
    tabs,
    selectedRestaurant,
    products,
    catOrder,
    user,
    selectedRestId,
    roles,
    userPermissions,
    isClassExtra,
}) => {
    // ---- Table / seating state ----
    const [mainCategory, setMainCategory] = useState('');
    const [activeTable, setActiveTable] = useState('');

    // ---- Menu browsing state ----
    const [selectedMenuCat, setSelectedMenuCat] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null);

    // ---- Cart UI state ----
    const [mobileCartOpen, setMobileCartOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [checkedGST, setCheckedGST] = useState(true);
    const [discount, setDiscount] = useState(0);
    const [serviceType, setServiceType] = useState<OrderServiceType>(DEFAULT_ORDER_SERVICE_TYPE);

    // ---- Customer form state ----
    const [customerDetails, setCustomerDetails] = useState<CustomerDetails>({
        customerName: '',
        customerPhone: '',
        billingName: '',
        gstNumber: '',
        billingAddress: '',
    });

    // ---- Variant selection state ----
    const [variantMap, setVariantMap] = useState<VariantMap>({});

    const dispatch = useDispatch();
    const params = useParams();
    const navigate = useNavigate();
    const searchRef = useRef<HTMLInputElement>(null);
    const isOnline = useOnlineStatus();

    // ---- Permissions ----
    const canReadTestBilling = hasPermission(userPermissions, 'testBilling', 'read');
    const canPrintKOT = hasPermission(userPermissions, 'printKOT', 'read');

    // ---- Initialize tabs ----
    useEffect(() => {
        if (tabs) {
            const sorted = Object.entries(tabs).sort().reverse();
            if (sorted[0]?.[0]) {
                setMainCategory(sorted[0][0]);
                setActiveTable(sorted[0][0] + '-1');
            }
        }
    }, [tabs]);

    // ---- Initialize menu category ----
    useEffect(() => {
        const first = (catOrder?.length && catOrder[0]) || (products && Object.keys(products)[0]) || '';
        setSelectedMenuCat(first);
    }, [catOrder, products]);

    // ---- Search ----
    useEffect(() => {
        if (searchQuery.trim()) {
            setSearchResults(handleSearchFoodItems(products, searchQuery));
        } else {
            setSearchResults(null);
        }
    }, [searchQuery, products]);

    // ---- Keyboard shortcut: Ctrl+K to search, Esc to clear ----
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                searchRef.current?.focus();
            }
            if (e.key === 'Escape') {
                setSearchQuery('');
                searchRef.current?.blur();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    const tableScopedStorageKey = `${params?.restaurantId || selectedRestId || 'default'}::${activeTable || 'table-1'}`;

    useEffect(() => {
        try {
            const allStoredInputs = JSON.parse(localStorage.getItem(CART_INPUTS_STORAGE_KEY) || '{}');
            const currentTableInputs = allStoredInputs?.[tableScopedStorageKey] || {};
            setServiceType(normalizeOrderServiceType(currentTableInputs?.serviceType));
        } catch {
            setServiceType(DEFAULT_ORDER_SERVICE_TYPE);
        }
    }, [tableScopedStorageKey]);

    const handleServiceTypeChange = useCallback((next: OrderServiceType) => {
        setServiceType(next);
        try {
            const allStoredInputs = JSON.parse(localStorage.getItem(CART_INPUTS_STORAGE_KEY) || '{}');
            allStoredInputs[tableScopedStorageKey] = {
                ...(allStoredInputs[tableScopedStorageKey] || {}),
                serviceType: next,
            };
            localStorage.setItem(CART_INPUTS_STORAGE_KEY, JSON.stringify(allStoredInputs));
        } catch {
            /* ignore storage errors */
        }
    }, [tableScopedStorageKey]);

    // ---- Derived data ----
    const badgeCounts = useMemo(() => computeBadgeCounts(cart), [cart]);
    const tableCart = useMemo<TableCart>(() => cart?.[activeTable] || {}, [cart, activeTable]);
    const activeCartItems = useMemo(() => getActiveCartItems(tableCart), [tableCart]);
    const menuCategories = useMemo(() => getMenuCategories(catOrder, products), [catOrder, products]);
    const menuItems = useMemo(() => getMenuItems(products, selectedMenuCat), [products, selectedMenuCat]);

    const billing = useMemo(
        () =>
            computeBillingSummary(
                tableCart,
                !!checkedGST,
                selectedRestaurant?.gstRate || 0,
                selectedRestaurant?.serviceRate || 0,
                discount,
            ),
        [tableCart, checkedGST, selectedRestaurant?.gstRate, selectedRestaurant?.serviceRate, discount],
    );

    // ---- Handlers ----
    const handleCategoryChange = useCallback((cat: string) => {
        setMainCategory(cat);
        setActiveTable(cat + '-1');
    }, []);

    const handleAddToCart = useCallback(
        (item: FoodItem, category: string, name: string) => {
            addItemToCart(item, category, name, variantMap, tableCart, dispatch, activeTable, params?.restaurantId);
        },
        [variantMap, tableCart, dispatch, activeTable, params?.restaurantId],
    );

    const handleUpdateQty = useCallback(
        (key: string, val: CartItem, delta: number) => {
            updateItemQty(key, val, delta, dispatch, activeTable, params?.restaurantId);
        },
        [dispatch, activeTable, params?.restaurantId],
    );

    const handleRemoveItem = useCallback(
        (key: string) => {
            removeItem(key, dispatch, activeTable, params?.restaurantId);
        },
        [dispatch, activeTable, params?.restaurantId],
    );

    const handleDescriptionChange = useCallback(
        (itemKey: string, description: string) => {
            const existing = tableCart[itemKey];
            if (existing) {
                dispatch(addToCartData({ [itemKey]: { ...existing, itemDescription: description } }, activeTable, params?.restaurantId));
            }
        },
        [tableCart, dispatch, activeTable, params?.restaurantId],
    );

    const handleVariantChange = useCallback((compositeKey: string, variant: Variant) => {
        setVariantMap((prev) => ({
            ...prev,
            [compositeKey]: { value: variant.cost, label: `₹${variant.cost} / ${variant.size}` },
        }));
    }, []);

    const handleMergeTable = useCallback(() => {
        executeMergeTable({ tabs, activeTable, fullCart: cart, tableCart, restaurantId: params?.restaurantId, dispatch });
    }, [tabs, activeTable, cart, tableCart, params?.restaurantId, dispatch]);

    const handleCheckout = useCallback(() => {
        setIsLoading(true);
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');

        if (userData?.phoneNumber) {
            const orderDetails = buildOrderDetails({
                tableCart,
                activeTable,
                billing,
                user,
                roles,
                selectedRestId,
                selectedRestaurant,
                restaurantId: params?.restaurantId || '',
                customer: customerDetails,
                serviceType,
            });
            submitOrder(orderDetails, dispatch, navigate, params?.restaurantId, activeTable, 'admin');
        } else {
            navigate(`/login?redirect=/restaurant/${params?.restaurantId}`);
        }
    }, [tableCart, activeTable, billing, user, roles, selectedRestId, selectedRestaurant, params, customerDetails, serviceType, dispatch, navigate]);

    const handleDiscount = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = Number(e.target.value);
            setDiscount(val > 0 ? (val > billing.totalCost ? 0 : val) : 0);
        },
        [billing.totalCost],
    );

    const handleCustomerChange = useCallback((field: keyof CustomerDetails, value: string) => {
        setCustomerDetails((prev) => ({ ...prev, [field]: value }));
    }, []);

    return (
        <div className={`posv2 ${isClassExtra ? 'posv2--extra-tabs' : ''}`}>
            {/* Top bar: Table categories + numbered tables */}
            <TableTopBar
                tabs={tabs}
                mainCategory={mainCategory}
                activeTable={activeTable}
                badgeCounts={badgeCounts}
                onCategoryChange={handleCategoryChange}
                onTableChange={setActiveTable}
            />

            <div className="posv2-body">
                {/* Left: Menu panel */}
                <div className="posv2-menu">
                    {!searchQuery && (
                        <aside className="posv2-menu__category-rail" aria-label="Menu categories">
                            <CategoryChips
                                layout="rail"
                                categories={menuCategories}
                                selectedCategory={selectedMenuCat}
                                onSelect={setSelectedMenuCat}
                            />
                        </aside>
                    )}

                    <div className="posv2-menu__main">
                        <SearchBar
                            value={searchQuery}
                            onChange={setSearchQuery}
                            onClear={() => setSearchQuery('')}
                            inputRef={searchRef}
                        />

                        {!searchQuery && (
                            <div className="posv2-menu__categories-mobile">
                                <CategoryChips
                                    layout="scroll"
                                    categories={menuCategories}
                                    selectedCategory={selectedMenuCat}
                                    onSelect={setSelectedMenuCat}
                                />
                            </div>
                        )}

                        {/* Search results (each result carries its own category) */}
                        {searchQuery && searchResults && searchResults.length > 0 && (
                            <SearchResultsGrid
                                results={searchResults}
                                tableCart={tableCart}
                                variantMap={variantMap}
                                onAddToCart={handleAddToCart}
                                onUpdateQty={handleUpdateQty}
                                onVariantChange={handleVariantChange}
                            />
                        )}

                        {searchQuery && searchResults && searchResults.length === 0 && (
                            <div className="posv2-empty">
                                <p>No items found for &ldquo;{searchQuery}&rdquo;</p>
                            </div>
                        )}

                        {/* Category items */}
                        {!searchQuery && (
                            <MenuGrid
                                items={menuItems}
                                category={selectedMenuCat}
                                tableCart={tableCart}
                                variantMap={variantMap}
                                onAddToCart={handleAddToCart}
                                onUpdateQty={handleUpdateQty}
                                onVariantChange={handleVariantChange}
                            />
                        )}
                    </div>
                </div>

                {/* Right: Cart panel */}
                <CartPanel
                    activeTable={activeTable}
                    tableCart={tableCart}
                    activeCartItems={activeCartItems}
                    billing={billing}
                    mobileCartOpen={mobileCartOpen}
                    isLoading={isLoading}
                    isOnline={isOnline}
                    canReadTestBilling={canReadTestBilling}
                    canPrintKOT={canPrintKOT}
                    checkedGST={checkedGST}
                    showGstField={!!selectedRestaurant?.permissions?.foodGST}
                    customerDetails={customerDetails}
                    serviceType={serviceType}
                    selectedRestId={selectedRestId}
                    roles={roles}
                    onServiceTypeChange={handleServiceTypeChange}
                    onToggleGST={setCheckedGST}
                    onCloseMobile={() => setMobileCartOpen(false)}
                    onMergeTable={handleMergeTable}
                    onUpdateQty={handleUpdateQty}
                    onRemoveItem={handleRemoveItem}
                    onDescriptionChange={handleDescriptionChange}
                    onCheckout={handleCheckout}
                    onCustomerChange={handleCustomerChange}
                    onDiscountChange={handleDiscount}
                />
            </div>

            {/* Mobile FAB */}
            <CartFab
                itemCount={activeCartItems.length}
                grandTotal={billing.grandTotal}
                hasCost={billing.totalCost > 0}
                onOpen={() => setMobileCartOpen(true)}
            />
        </div>
    );
};

const mapStateToProps = (state: any) => {
    const { cart, common, user } = state;
    return {
        cart: (cart && cart[common?.selectedRestaurant?.docId]) || {},
        tabs: common?.selectedRestaurant?.tabs || {},
        selectedRestaurant: common?.selectedRestaurant || {},
        products: common?.selectedRestaurant?.menu || {},
        catOrder: common?.selectedRestaurant?.categoryOrder || [],
        user: user?.user,
        selectedRestId: common?.selectedRestaurant?.docId || '',
        roles: user?.user?.roles || {},
        userPermissions: user?.user?.roles
            ? { ...common?.globalConfig?.permissions, ...(user?.user?.roles[common?.selectedRestaurant?.docId]?.permissions) }
            : {},
        isClassExtra: Object.entries(common?.selectedRestaurant?.tabs || {}).length > 1,
    };
};

export default connect(mapStateToProps)(POSV2);
